import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import {
  Stack,
  Divider,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
} from '@mui/material';
import DatePicker from '@mui/lab/DatePicker';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import dayjs from 'dayjs';

const UNIT_LABEL = { day: 'วัน', month: 'เดือน', year: 'ปี' };

// ความสูงเดียวกันทุกช่อง (ToggleButton/TextField/DatePicker) ในแถวนี้ — ปกติ ToggleButton กับ TextField
// size="small" ของ MUI สูงไม่เท่ากัน (ToggleButton เตี้ยกว่าเพราะ padding แนวตั้งน้อยกว่า default ของ
// TextField ~40px) ทำให้แถวดูเบี้ยว ต้อง fix height ตรงๆ ทุกตัวให้เท่ากันแทนปล่อยตาม default
export const CONTROL_HEIGHT = 35;

// แตกช่วงวันที่ระหว่าง from-to เป็นปี/เดือน/วัน แบบละเอียด (ไม่ปัดทิ้งเศษ) เช่น 2 เดือน 15 วัน
function exactBreakdown(from, to) {
  let cursor = dayjs(from);
  const target = dayjs(to);
  const years = target.diff(cursor, 'year');
  cursor = cursor.add(years, 'year');
  const months = target.diff(cursor, 'month');
  cursor = cursor.add(months, 'month');
  const days = target.diff(cursor, 'day');
  return { years, months, days };
}

function formatBreakdown({ years, months, days }) {
  const parts = [];
  if (years > 0) parts.push(`${years} ปี`);
  if (months > 0) parts.push(`${months} เดือน`);
  if (days > 0 || parts.length === 0) parts.push(`${days} วัน`);
  return parts.join(' ');
}

// ป้ายเล็กสีเทาหน้ากลุ่มควบคุมแต่ละกลุ่ม (ค้นหาจาก/รูปแบบ/กำหนดค่า) — แยกกลุ่มด้วยป้าย+เส้นคั่นแนวตั้ง
// แทนการขึ้นบรรทัดใหม่ทีละกลุ่ม เพื่อประหยัดพื้นที่แนวตั้งที่สุด (ทุกกลุ่มยังอยู่แถวเดียวกันถ้าจอกว้างพอ)
function GroupLabel({ children }) {
  return (
    <Typography
      variant="caption"
      fontWeight={700}
      color="text.secondary"
      sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
    >
      {children}
    </Typography>
  );
}

// ช่วงวันที่สำหรับค้นหารายการเบิก — โหมด "ย้อนหลัง N วัน/เดือน/ปี จากวันที่กำหนด" หรือ "เลือกช่วง start-end เอง"
// bound ด้วย minDate/maxDate (ช่วงที่มีรายการเบิกจริงในระบบของ tab นั้นๆ) ถ้าระบุมา — กันไม่ให้เลือกย้อนหลังเกินกว่าที่มีข้อมูลจริง
// ไม่มีปุ่มค้นหาของตัวเอง — parent เรียก ref.getValue() ตอนกดปุ่มค้นหารวมด้านล่าง panel
//
// จัดเป็น 3 กลุ่มควบคุมชัดเจน (ค้นหาจาก field ไหน / รูปแบบการเลือก / กำหนดค่าจริง) บนแถวเดียวกัน คั่นด้วยป้าย+
// เส้นแนวตั้ง แล้วมีแถวผลลัพธ์ (ช่วงวันที่ที่คำนวณได้จริง) อยู่ใต้ — ยุบรวม field-selector (วันที่ขอรับเงิน/
// วันที่สร้างรายการ) ที่เดิมแยก render อยู่ในไฟล์ tab แต่ละไฟล์ (ExpensesTabArchive.js เป็นต้น) เข้ามาไว้ในนี้
// เป็นจุดเดียว ผ่าน prop dateField/onDateFieldChange (ไม่ส่งมา = ไม่โชว์กลุ่มนี้)
//
// onMaxLookbackChange (ถ้าส่งมา): เรียกทุกครั้งที่ข้อความ "เลือกย้อนหลังได้สูงสุด..." เปลี่ยน (หรือ null ถ้า
// ไม่เข้าเงื่อนไข mode 'quick' + มี minDate) — ให้ parent เอาไปโชว์เองนอก component นี้ (เช่นแถวหัว "ตัวกรอง")
// แทนที่จะ render ในนี้ตรงๆ — ต้องส่ง state setter ที่ reference คงที่ (เช่น setXxx จาก useState ตรงๆ) ห้าม
// wrap ด้วย arrow function ใหม่ทุก render ไม่งั้น effect ด้านล่างจะ loop
const ExpensesDateRangePicker = forwardRef(
  ({ value, minDate, maxDate, onMaxLookbackChange, dateField, onDateFieldChange }, ref) => {
    const [mode, setMode] = useState('quick');
    const [unit, setUnit] = useState('month');
    const [count, setCount] = useState(1);
    const [endDate, setEndDate] = useState(value?.[1] || maxDate || new Date());
    const [customStart, setCustomStart] = useState(value?.[0] || minDate || null);
    const [customEnd, setCustomEnd] = useState(value?.[1] || maxDate || null);
    const [singleDate, setSingleDate] = useState(value?.[0] || maxDate || new Date());
    const endDateTouched = useRef(false);
    const customEndTouched = useRef(false);
    const singleDateTouched = useRef(false);

    // maxDate มาถึงทีหลัง (ยิง async ตอน mount) — ถ้า user ยังไม่ได้แก้ "ถึงวันที่"/"วันที่" เอง ให้ sync
    // เป็นวันที่รายการเบิกล่าสุดของ tab นี้
    useEffect(() => {
      if (!value?.[1] && maxDate) {
        if (!endDateTouched.current) setEndDate(maxDate);
        if (!customEndTouched.current) setCustomEnd(maxDate);
        if (!singleDateTouched.current) setSingleDate(maxDate);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [maxDate]);

    const clamp = (date) => {
      let d = dayjs(date);
      if (minDate && d.isBefore(dayjs(minDate))) d = dayjs(minDate);
      if (maxDate && d.isAfter(dayjs(maxDate))) d = dayjs(maxDate);
      return d;
    };

    // ย้อนหลังจากวันที่เลือกได้สูงสุดกี่หน่วย (ตาม unit ปัจจุบัน) ก่อนจะชนขอบเขตข้อมูลจริง (minDate)
    const maxCount = minDate
      ? Math.max(1, dayjs(endDate).diff(dayjs(minDate), unit))
      : 999;

    // ข้อความ "เลือกย้อนหลังได้สูงสุด..." — ส่งออกทาง callback ให้ parent โชว์เอง (เช่นแถวหัว "ตัวกรอง")
    // ต้องโชว์ทุก mode (ไม่ใช่แค่ 'quick') เลยนับ breakdown จาก minDate-maxDate (ขอบเขตข้อมูลจริงทั้งหมด
    // ของ tab นี้) แทน minDate-endDate เดิม — endDate มีความหมายเฉพาะตอน mode 'quick' เท่านั้น ใช้ไม่ได้กับ
    // mode อื่น ส่วน maxDate เป็นค่าคงที่ไม่ขึ้นกับ mode ที่เลือกอยู่ตอนนี้ เหมาะจะใช้แทน
    const maxLookbackText =
      minDate && maxDate
        ? `เลือกย้อนหลังได้สูงสุด ${formatBreakdown(exactBreakdown(minDate, maxDate))} (มีรายการเบิกตั้งแต่ ${dayjs(minDate).format('DD/MM/YYYY')})`
        : null;

    useEffect(() => {
      onMaxLookbackChange?.(maxLookbackText);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [maxLookbackText]);

    // endDate/unit/minDate เปลี่ยน แล้ว count เดิมเกินขอบเขตใหม่ — เคลมให้อยู่ในช่วงที่เลือกได้จริง กันเลือกมั่วๆ
    useEffect(() => {
      setCount((prev) => Math.min(Math.max(prev, 1), maxCount));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [unit, endDate, minDate]);

    useImperativeHandle(
      ref,
      () => ({
        // mode 'all' คืน null ตรงๆ — parent (ไฟล์ tab ที่เรียกใช้) ไม่ set dateStart/dateEnd ใน query เลย
        // ถ้าค่าเป็น null ทำให้ backend (expenses.service.js createPipeline) ข้าม $match ช่วงวันที่ไปเลย
        // เหลือแค่ scope ของ tab bucket เอง (today/backlog/future/archive/all) — ได้ผลลัพธ์ทุกช่วงเวลาจริงๆ
        getValue: () => {
          if (mode === 'all') return null;
          if (mode === 'quick') {
            const end = clamp(endDate);
            const start = clamp(end.subtract(count || 1, unit));
            return [start.startOf('day').toDate(), end.endOf('day').toDate()];
          }
          if (mode === 'single') {
            if (!singleDate) return null;
            const d = clamp(singleDate);
            return [d.startOf('day').toDate(), d.endOf('day').toDate()];
          }
          if (!customStart || !customEnd) return null;
          return [
            clamp(customStart).startOf('day').toDate(),
            clamp(customEnd).endOf('day').toDate(),
          ];
        },
      }),
      [mode, unit, count, endDate, customStart, customEnd, singleDate, minDate, maxDate],
    );

    // ช่วงวันที่ที่กำลังตั้งค่าอยู่ตอนนี้ (ยังไม่ต้องกดค้นหา) — โชว์ให้เห็นสดๆ ว่าจะค้นตั้งแต่วันไหนถึงวันไหน
    // mode 'all' ไม่ได้ "ไม่มีช่วง" จริงๆ — แค่ไม่กรอง แปลว่าช่วงจริงคือ minDate-maxDate (ขอบเขตข้อมูลจริง
    // ของ tab นี้) ที่ parent ส่งมาให้ ถ้ามีครบทั้งคู่ก็โชว์เป็นช่วงได้เหมือน mode อื่น ไม่ต้องมี branch แยก
    const previewRange =
      mode === 'all'
        ? minDate && maxDate ? [dayjs(minDate), dayjs(maxDate)] : null
        : mode === 'quick'
        ? [clamp(endDate).subtract(count || 1, unit), clamp(endDate)]
        : mode === 'single'
        ? singleDate ? [clamp(singleDate), clamp(singleDate)] : null
        : customStart && customEnd ? [clamp(customStart), clamp(customEnd)] : null;

    // นับระยะเวลาของ previewRange เป็น "X ปี Y เดือน Z วัน" — mode 'single' เป็นวันเดียวกันทั้ง start/end
    // เลยนับแบบ breakdown ปกติจะได้ 0 วันเสมอ (ผิดเจตนา ควรนับเป็น 1 วัน) เลย hardcode แยกไว้
    const previewDuration = !previewRange
      ? null
      : mode === 'single'
      ? '1 วัน'
      : formatBreakdown(exactBreakdown(previewRange[0], previewRange[1]));

    const vDivider = <Divider orientation="vertical" flexItem sx={{ my: 0.25 }} />;

    return (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Stack spacing={0.5}>
          <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" useFlexGap>
            {onDateFieldChange && (
              <>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                  <GroupLabel>ค้นหาจาก</GroupLabel>
                  <ToggleButtonGroup
                    value={dateField}
                    exclusive
                    onChange={(_, val) => { if (val) onDateFieldChange(val); }}
                    size="small"
                  >
                    <ToggleButton value="date" sx={{ fontSize: '0.78rem', px: 1.5, height: CONTROL_HEIGHT }}>วันที่ขอรับเงิน</ToggleButton>
                    <ToggleButton value="createdAt" sx={{ fontSize: '0.78rem', px: 1.5, height: CONTROL_HEIGHT }}>วันที่สร้างรายการ</ToggleButton>
                  </ToggleButtonGroup>
                </Stack>
                {vDivider}
              </>
            )}

            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
              <GroupLabel>รูปแบบ</GroupLabel>
              <ToggleButtonGroup
                value={mode}
                exclusive
                onChange={(_, val) => { if (val) setMode(val); }}
                size="small"
              >
                <ToggleButton value="quick" sx={{ fontSize: '0.78rem', px: 1.5, height: CONTROL_HEIGHT }}>ย้อนหลัง</ToggleButton>
                <ToggleButton value="custom" sx={{ fontSize: '0.78rem', px: 1.5, height: CONTROL_HEIGHT }}>กำหนดเอง</ToggleButton>
                <ToggleButton value="all" sx={{ fontSize: '0.78rem', px: 1.5, height: CONTROL_HEIGHT }}>ทั้งหมด</ToggleButton>
                <ToggleButton value="single" sx={{ fontSize: '0.78rem', px: 1.5, height: CONTROL_HEIGHT }}>ดูวันเดียว</ToggleButton>
              </ToggleButtonGroup>
            </Stack>

            {mode !== 'all' && (
              <>
                {vDivider}
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ flexShrink: 0 }}>
                  <GroupLabel>กำหนดค่า</GroupLabel>
                  {mode === 'quick' && (
                    <>
                      <TextField
                        type="number"
                        size="small"
                        value={count}
                        onChange={(e) => {
                          const v = parseInt(e.target.value, 10);
                          if (Number.isNaN(v)) return;
                          setCount(Math.min(Math.max(v, 1), maxCount));
                        }}
                        inputProps={{ min: 1, max: maxCount }}
                        sx={{ width: 72, '& .MuiOutlinedInput-root': { height: CONTROL_HEIGHT } }}
                      />
                      <ToggleButtonGroup
                        value={unit}
                        exclusive
                        onChange={(_, val) => { if (val) setUnit(val); }}
                        size="small"
                      >
                        {['day', 'month', 'year'].map((u) => (
                          <ToggleButton key={u} value={u} sx={{ px: 1.5, fontSize: '0.78rem', height: CONTROL_HEIGHT }}>
                            {UNIT_LABEL[u]}
                          </ToggleButton>
                        ))}
                      </ToggleButtonGroup>
                      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>ถึงวันที่</Typography>
                      <DatePicker
                        value={endDate}
                        onChange={(newValue) => { endDateTouched.current = true; setEndDate(newValue); }}
                        minDate={minDate}
                        maxDate={maxDate}
                        renderInput={(params) => (
                          <TextField {...params} size="small" sx={{ width: 150, '& .MuiOutlinedInput-root': { height: CONTROL_HEIGHT } }} />
                        )}
                      />
                    </>
                  )}
                  {mode === 'custom' && (
                    <>
                      <DatePicker
                        label="จากวันที่"
                        value={customStart}
                        onChange={(newValue) => setCustomStart(newValue)}
                        minDate={minDate}
                        maxDate={maxDate}
                        renderInput={(params) => (
                          <TextField {...params} size="small" sx={{ width: 150, '& .MuiOutlinedInput-root': { height: CONTROL_HEIGHT } }} />
                        )}
                      />
                      <Typography variant="caption" color="text.secondary">ถึง</Typography>
                      <DatePicker
                        label="ถึงวันที่"
                        value={customEnd}
                        onChange={(newValue) => { customEndTouched.current = true; setCustomEnd(newValue); }}
                        minDate={minDate}
                        maxDate={maxDate}
                        renderInput={(params) => (
                          <TextField {...params} size="small" sx={{ width: 150, '& .MuiOutlinedInput-root': { height: CONTROL_HEIGHT } }} />
                        )}
                      />
                    </>
                  )}
                  {mode === 'single' && (
                    <DatePicker
                      value={singleDate}
                      onChange={(newValue) => { singleDateTouched.current = true; setSingleDate(newValue); }}
                      minDate={minDate}
                      maxDate={maxDate}
                      renderInput={(params) => (
                        <TextField {...params} size="small" sx={{ width: 150, '& .MuiOutlinedInput-root': { height: CONTROL_HEIGHT } }} />
                      )}
                    />
                  )}
                </Stack>
              </>
            )}
          </Stack>

          {/* ผลลัพธ์ — ช่วงวันที่ที่กำลังตั้งค่าอยู่ตอนนี้จริงๆ (ยังไม่ต้องกดค้นหา) คำนวณสดจากกลุ่มด้านบน */}
          <Stack direction="row" spacing={0.75} alignItems="baseline" flexWrap="wrap" useFlexGap>
            <GroupLabel>ผลลัพธ์</GroupLabel>
            {mode === 'all' && (
              <Typography variant="body2" fontWeight={600} color="primary.main">
                ค้นหาทุกช่วงเวลา (ไม่จำกัดวันที่)
              </Typography>
            )}
            {mode !== 'all' && previewRange && mode === 'single' && (
              <Typography variant="body2" fontWeight={600} color="primary.main">
                วันที่ {previewRange[0].format('DD/MM/YYYY')} (นับได้ {previewDuration})
              </Typography>
            )}
            {mode !== 'all' && previewRange && mode !== 'single' && (
              <Typography variant="body2" fontWeight={600} color="primary.main">
                จากวันที่ {previewRange[0].format('DD/MM/YYYY')} ถึงวันที่ {previewRange[1].format('DD/MM/YYYY')} (นับได้ {previewDuration})
              </Typography>
            )}
            {mode !== 'all' && !previewRange && (
              <Typography variant="body2" color="text.disabled">ยังไม่ได้เลือกวันที่ครบ</Typography>
            )}
          </Stack>
        </Stack>
      </LocalizationProvider>
    );
  },
);

export default ExpensesDateRangePicker;
